import { COMMA, ENTER, SPACE } from "@angular/cdk/keycodes";
import { NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, ViewEncapsulation, WritableSignal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute } from "@angular/router";
import { TranslocoModule, TranslocoService } from "@ngneat/transloco";
import { eachDayOfInterval, eachWeekOfInterval, endOfMonth, format, isSameMonth, nextSunday, parse, startOfMonth, startOfToday, subDays, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import { firstValueFrom } from "rxjs";
import { DialogsService } from "../dialogs.service";
import { LocalizeDatePipe } from "../../core/pipes/localizeDate.pipe";
import { ConceptField, ConceptSummaryColumn, ConceptSummaryRow, ConceptSummaryRowStatus, ContractWorkSummaryRow, MonthlyContractWorkConceptSummary, WorkplaceWorkSummaryDayCell } from "../../modules/admin/endpoints/endpoints.types";
import { Net2BeepleLocator } from "../../core/dtype/dtype.types";
import { EndpointsService } from "../../modules/admin/endpoints/endpoints.service";
import { FuseConfirmationService } from "../../../@fuse/services/confirmation/confirmation.service";
import { LocalizeService } from "../../core/pipes/localize.service";
import { TimeFormat } from "../../core/enums/time-format.enum";
import { StorageKeysEnum } from "../../core/enums/storage.keys.enum";
import { FlushWorkplaceWorkConceptSummaryReply, RegularizeHourBagWithConceptsReply } from "../../core/http/reply.model";
import { FlushDailyWorkplaceWorkConceptSummaryRequest, FlushMonthlyContractWorkConceptSummaryRequest, FlushMonthlyWorkplaceWorkConceptSummaryRequest } from "../../core/http/request.model";
import { Utils } from "../../core/utils/utils";

@Component({
    selector: 'app-edit-concept-summary-dialog',
    templateUrl: './edit-concept-summary-dialog.component.html',
    styleUrls: ['./edit-concept-summary-dialog.component.scss'],
    imports: [FormsModule, LocalizeDatePipe, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSlideToggleModule, NgClass, ReactiveFormsModule, TranslocoModule, MatTooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class EditConceptSummaryDialogComponent implements OnInit {
    readonly separatorKeysCodes = [ENTER, COMMA, SPACE] as const;

    public data: { row: ConceptSummaryRow | MonthlyContractWorkConceptSummary, concepts: ConceptField[], locator: Net2BeepleLocator, dayOfMonth?: string, period: string, year: string } = inject(MAT_DIALOG_DATA);
    public dialogRef = inject(MatDialogRef<EditConceptSummaryDialogComponent>);
    private dialogsService = inject(DialogsService);
    private endpointsService = inject(EndpointsService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private localize = inject(LocalizeService);
    private transloco = inject(TranslocoService);
    private activatedRoute = inject(ActivatedRoute);

    displayRow = signal(this.data.row);
    displayConcepts = signal(this.data.concepts);

    conceptsForm = new FormArray<FormGroup<{
        value: FormControl<number>,
        calculatedValue: FormControl<number>
    }>>([]);

    days = signal<Date[]>([]);

    timeFormat: WritableSignal<TimeFormat>; 

    queryParams = toSignal(this.activatedRoute.queryParams);
    locator = computed(() => {
        const queryParams = this.queryParams();
        if (queryParams) {
            const [locator, stringLocator] = this.endpointsService.buildLocatorFromParams(queryParams);
            return locator;
        }
    });

    isWithinCurrentOrPreviousMonth: boolean;

    monthYearString = format(new Date(Number(this.data.year), Number(this.data.period) - 1), "MMMM 'de' yyyy", { locale: es });
    monthYearTitle = this.monthYearString.charAt(0).toUpperCase() + this.monthYearString.slice(1);

    private hasRowChanged: boolean = false;
    private isFirsRowChange = true;

    constructor() {
        effect(() => {
            this.displayRow();

            if (this.isFirsRowChange) {
                this.isFirsRowChange = false;
                return;
            }

            this.hasRowChanged = true;
        });
    }

    async ngOnInit() {
        this.timeFormat = signal<TimeFormat>(localStorage.getItem(StorageKeysEnum.timeFormat) as TimeFormat);
        if (!this.timeFormat()) {
            this.timeFormat.set(TimeFormat.HOURS_MINUTES);
            localStorage.setItem(StorageKeysEnum.timeFormat, this.timeFormat());
        }

        const now = new Date();     // Meses con valores [0, 11]
        const previous = subMonths(now, 1);
        const selectedMonth: number = Number(this.data.period);
        const selectedYear: number = Number(this.data.year);
        this.isWithinCurrentOrPreviousMonth = (now.getMonth() + 1 === selectedMonth && now.getFullYear() === selectedYear) 
            || (previous.getMonth() + 1 === selectedMonth && previous.getFullYear() === selectedYear);

        for (const concept of this.data.concepts) {
            if (this.data.row.cols.has(concept.conceptId)) {
                this.conceptsForm.push(new FormGroup({
                    value: new FormControl(this.data.row.cols.get(concept.conceptId).value),
                    calculatedValue: new FormControl({ value: this.data.row.cols.get(concept.conceptId).calculatedValue, disabled: true })
                }));
            } else {
                this.conceptsForm.push(new FormGroup({
                    value: new FormControl({ value: undefined, disabled: true }),
                    calculatedValue: new FormControl({ value: undefined, disabled: true })
                }));
            }
        }

        const date = parse(`${this.data.year}-${this.data.period}-01`, 'yyyy-MM-dd', startOfToday());
        const weeks = eachWeekOfInterval({ start: startOfMonth(date), end: endOfMonth(date) }, { weekStartsOn: 1 });
        const days: Date[] = [];
        weeks.forEach((week, index) => {
            if (index < weeks.length - 1) {
                days.push(...eachDayOfInterval({ start: week, end: subDays(weeks[index + 1], 1) }));
            } else {
                days.push(...eachDayOfInterval({ start: week, end: nextSunday(week) }));
            }
        });
        this.days.set(days);
    }

    closeDialog() {
        this.dialogRef.close();
    }

    hideDay(day: Date) {
        const date = parse(`${this.data.year}-${this.data.period}-01`, 'yyyy-MM-dd', startOfToday());
        return !isSameMonth(day, date);
    }

    async accept() {
        const confirm = await this.confirm();
        if (confirm) {
            const concepts: { oid: string; net4ConceptId: number, value: any }[] = [];
            this.conceptsForm.controls.forEach((group, index) => {
                if (this.data.row.cols.has(this.data.concepts[index].conceptId)) {
                    concepts.push({
                        oid: this.data.row.cols.get(this.data.concepts[index].conceptId).oid,
                        net4ConceptId: this.data.row.cols.get(this.data.concepts[index].conceptId).net4ConceptId,
                        value: group.get('value').value
                    });
                }
            });
            try {
                let reply: FlushWorkplaceWorkConceptSummaryReply;
                if (this.data.row.summaryRow) {
                    if (this.rowIsConceptSummary(this.data.row)) {
                        const request: FlushMonthlyWorkplaceWorkConceptSummaryRequest = {
                            locator: this.data.locator,
                            key: this.data.row.key,
                            concepts
                        };
                        reply = await firstValueFrom(this.endpointsService.flushMonthlyWorkplaceWorkConceptSummary(request));
                    } else {
                        const request: FlushMonthlyContractWorkConceptSummaryRequest = {
                            locator: this.data.locator,
                            key: this.data.row.key,
                            concepts
                        };
                        reply = await firstValueFrom(this.endpointsService.flushMonthlyContractWorkConceptSummary(request));
                    }
                } else {
                    const request: FlushDailyWorkplaceWorkConceptSummaryRequest = {
                            locator: this.data.locator,
                            key: this.data.row.key,
                            date: `${this.data.year}-${this.data.period}-${this.data.dayOfMonth}`,
                            concepts
                        };
                    reply = await firstValueFrom(this.endpointsService.flushDailyWorkplaceWorkConceptSummary(request));
                }

                let rowStatus: ConceptSummaryRowStatus = reply.rowStatus;
                let updatedRow = this.displayRow();
                for (const res of concepts) {
                    updatedRow.cols.get(res.net4ConceptId).value = res.value;
                }

                updatedRow.rowStatus = rowStatus.status;
                updatedRow.approverDisplayName = rowStatus.approverDisplayName;
                updatedRow.approverSAMAccountName = rowStatus.approverSAMAccountName;
                updatedRow.approverTimestamp = rowStatus.approverTimestamp;
                updatedRow.approverUserId = rowStatus.approverUserId;
                this.displayRow.set({...updatedRow});

                this.dialogRef.close();
            } catch (error) {
                console.error(error);
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo && Object.keys(error?.error?.echo).length > 0 ? this.localize.transform(error?.error?.echo) : undefined,
                });
            }
        }
    }

    ngOnDestroy() {
        // row puede ser actualizado en openWorkerRegularizeHourBagDialog o en accept
        if (this.hasRowChanged) {
            this.dialogRef.close(this.displayRow());
        }
    }

    openWorkerAssignmentsDialog() {
        this.dialogsService.openWorkerAssignmentDialog({
            locator: this.data.locator,
            empresaId: this.data.row.empresaId,
            dayOfMonth: this.data.dayOfMonth,
            period: this.data.period,
            year: this.data.year,
            workplaceCode: this.rowIsConceptSummary(this.data.row) ? this.data.row.workplaceCode : undefined,
            trabajadorNet4Id: this.data.row.trabajadorNet4Id
        }, this.data.row.displayName);
    }

    rowIsConceptSummary(row: ConceptSummaryRow | MonthlyContractWorkConceptSummary) {
        return Utils.interfaceOf<ConceptSummaryRow>(row, 'workplaceCode');
    }

    displayRowAsConceptSummary() {
        return this.displayRow() as ConceptSummaryRow;
    }

    displayRowAsMonthlyContractWorkConceptSummary() {
        return this.displayRow() as MonthlyContractWorkConceptSummary;
    }

    getGuideWorkplace(row: MonthlyContractWorkConceptSummary) {
        return row.summaryRow.workplaceData.find(w => w.guide)?.workplaceCode ?? "" ;
    }

    getDayWorkedHours(date: Date) {
        return this.data.row.summaryRow.dayCell.find(d => d.date === format(date, 'yyyy-MM-dd'));
    }

    isHoliday(date: Date) {
        const day = this.data.row.summaryRow.dayCell.find(d => d.date === format(date, 'yyyy-MM-dd'));
        return day && day.holiday && !day.workable;
    }

    getMapValues(day: WorkplaceWorkSummaryDayCell) {
        return Object.entries(day.absenceMinutes);
    }

    minutesToHours(minutes: number) {
        if (!minutes) {
            return 0;
        }
        return Math.round(((minutes / 60) + Number.EPSILON) * 100) / 100;
    }

    minutesToText(minutes: number) {
        return Utils.minutesDisplay(minutes, this.timeFormat());
    }

    async openWorkerRegularizeHourBagDialog(row: ContractWorkSummaryRow) {
        const dialog = this.dialogsService.openWorkerRegularizeHourBagDialog(row, this.locator(), true);
        const reply: RegularizeHourBagWithConceptsReply = await firstValueFrom(dialog.afterClosed());
        const updatedRow: MonthlyContractWorkConceptSummary = reply?.inner?.rows?.find(r => r.key === this.displayRow().key);
        const updatedConceptFields: ConceptField[] = reply?.inner?.net4ConceptFields;

        if (updatedRow) {
            let transformedCols = new Map<number, ConceptSummaryColumn>();
            for (const col of Object.entries(updatedRow.cols)) {
                transformedCols.set(col[1].net4ConceptId, { ...col[1], oid: col[0] });
            }
            updatedRow.cols = transformedCols;
            this.displayRow.set(updatedRow);
        }
        if (updatedConceptFields && updatedRow) {
            this.displayConcepts.set(updatedConceptFields);
            updatedConceptFields.forEach((field, index) => {
                const newCalculatedValue  =  updatedRow.cols.get(field.conceptId)?.calculatedValue;
                console.log(newCalculatedValue)
                if (newCalculatedValue !== undefined && newCalculatedValue !== null) {
                    this.conceptsForm.controls[index].get('calculatedValue')?.setValue(newCalculatedValue);
                }
            });
        }
    }

    private confirm(confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate('conceptSummary.confirmSaveConcept'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}
